import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, TextField, Typography } from "@mui/material";
import { DataDialog } from "@app/components/atoms";
import { apiGetMachineDetailInfo, apiUpdateMachineInGroup } from "@app/api";
import { REGEX } from "@app/helpers";
import { setShowAlert, store } from "@app/store";
import { buildMachineUpdatePayloadFromDetail } from "./machineUpdatePayload.helper";
import { safeSliceGraphemes } from "@app/helpers";

const countGraphemes = (str: string): number =>
  [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(str)]
    .length;

const MEMO_MAX_LENGTH = 1000;
const MEMO_PREVIEW_LENGTH = 100;

type MachineMemoSectionProps = {
  memo?: string | null;
  groupId?: string;
  machineId?: string;
  onMemoUpdated: () => void;
};

export function MachineMemoSection({
  memo,
  groupId,
  machineId,
  onMemoUpdated,
}: MachineMemoSectionProps) {
  const { t } = useTranslation();
  const [editOpen, setEditOpen] = useState(false);
  const [draftMemo, setDraftMemo] = useState("");
  const [memoError, setMemoError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const trimmedMemo = (memo ?? "").trim();
  const previewMemo = safeSliceGraphemes(trimmedMemo, MEMO_PREVIEW_LENGTH);
  const hasMemo = trimmedMemo.length > 0;
  const draftLength = countGraphemes(draftMemo);
  const isDraftOverLimit = draftLength > MEMO_MAX_LENGTH;

  useEffect(() => {
    if (editOpen) {
      setDraftMemo(memo ?? "");
      setMemoError(null);
    }
  }, [editOpen, memo]);

  const handleMemoChange = (value: string) => {
    setDraftMemo(value);

    const isValid =
      REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) &&
      REGEX.emojiRegex.test(value);

    if (isValid) {
      setMemoError(null);
    } else if (!REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value)) {
      setMemoError(t("invalidCharacters"));
    } else if (!REGEX.emojiRegex.test(value)) {
      setMemoError(t("emojisarenotallowed"));
    }
  };

  const handlePreviewClick = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setDraftMemo(memo ?? "");
    setEditOpen(true);
  };

  const saveMemoWithFullPayload = async (memoValue: string | null) => {
    if (!groupId || !machineId) return;

    const response = await apiGetMachineDetailInfo(groupId, machineId);
    const detail = response.data?.data;

    if (!detail) {
      throw new Error("Missing machine detail");
    }

    const payload = buildMachineUpdatePayloadFromDetail(detail, memoValue, {
      omitPictureUrls: true,
    });
    await apiUpdateMachineInGroup(groupId, machineId, payload);
  };

  const handleSaveMemo = async () => {
    if (!groupId || !machineId || isDraftOverLimit || memoError) return;

    setIsSaving(true);
    try {
      const value = draftMemo.trim();
      await saveMemoWithFullPayload(value || null);
      store.dispatch(setShowAlert(t("machineInfo.memoUpdated")));
      setEditOpen(false);
      onMemoUpdated();
    } catch (error) {
      console.error("Failed to update machine memo:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Box className="machine-memo-section">
        <Box
          className="machine-memo-section__preview is-clickable"
          onClick={handlePreviewClick}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handlePreviewClick();
            }
          }}
        >
          <Typography className="machine-memo-section__title">
            {t("machineInfo.memo")}
          </Typography>

          {hasMemo ? (
            <p className="machine-memo-section__preview-text">
              {previewMemo}
              {previewMemo.length < trimmedMemo.length ? "…" : ""}
            </p>
          ) : (
            <Typography className="machine-memo-section__preview-empty">
              {t("machineInfo.memoEmptyHint")}
            </Typography>
          )}
        </Box>
      </Box>

      <DataDialog
        title={t("machineInfo.editMemo")}
        open={editOpen}
        setOpen={() => setEditOpen(false)}
        disableBackdropClick
        className="machine-memo-edit-dialog"
        dialogContentSx={{ pt: 0 }}
        dialogActions={
          <Box className="machine-memo-edit-dialog__actions">
            <Box className="machine-memo-edit-dialog__actions-right">
              <Button
                className="btn btn-no-border"
                onClick={() => setEditOpen(false)}
                disabled={isSaving}
              >
                {t("dialogReport.cancel")}
              </Button>
              <Button
                className="btn btn-primary"
                onClick={handleSaveMemo}
                disabled={isSaving || isDraftOverLimit || !!memoError}
              >
                {t("formAction.save")}
              </Button>
            </Box>
          </Box>
        }
      >
        <Typography className="machine-memo-edit-dialog__hint">
          {t("machineInfo.editMemoDescription")}
        </Typography>

        <Box className="machine-memo-edit-dialog__field-wrap">
          <TextField
            multiline
            minRows={6}
            maxRows={6}
            fullWidth
            value={draftMemo}
            onChange={(event) => {
              handleMemoChange(
                safeSliceGraphemes(event.target.value, MEMO_MAX_LENGTH),
              );
            }}
            error={isDraftOverLimit || !!memoError}
            className="machine-memo-edit-dialog__field"
          />
          {memoError ? (
            <span className="error-text fs-12 txt-red machine-memo-edit-dialog__error">
              {memoError}
            </span>
          ) : (
            <Typography
              className={`machine-memo-edit-dialog__counter${
                isDraftOverLimit ? " is-error" : ""
              }`}
            >
              {t("machineInfo.memoCharacterCount", {
                count: draftLength,
                max: MEMO_MAX_LENGTH,
              })}
            </Typography>
          )}
        </Box>
      </DataDialog>
    </>
  );
}
